import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  useEditMode,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const DISMISS_KEY = 'floorplanspopup:dismissed';
const OPEN_DELAY_MS = 600;

function PopupCard({ onClose }: { onClose?: () => void }) {
  const image = useContentToken('floorplanspopup.image');
  const heading = useContentToken('floorplanspopup.heading');

  return (
    <div className="relative w-full overflow-hidden bg-primary text-primary-foreground shadow-xl">
      <EditableToken path="floorplanspopup.image" type="image" as="div" label="Popup Image">
        <div className="relative aspect-[2/1] w-full bg-muted">
          <VibesitesImage
            url={image || ''}
            alt=""
            fill
            objectFit="cover"
            sizes="(min-width: 768px) 720px, 92vw"
            className="h-full w-full object-cover"
          />
        </div>
      </EditableToken>

      <div className="px-5 pb-8 pt-6 sm:px-6 sm:pb-10 sm:pt-8">
        <div id="floorplanspopup-heading">
          <EditableToken
            path="floorplanspopup.heading"
            as="h2"
            className="font-heading text-2xl font-semibold leading-tight tracking-tight sm:text-[2rem]"
          >
            {heading}
          </EditableToken>
        </div>

        <RichTextContent
          path="floorplanspopup.body"
          as="div"
          className="mt-3 font-body text-base font-light leading-[1.6] sm:text-[1.15rem] [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-80 [&_p+p]:mt-6 [&_strong]:font-bold"
        />
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground transition hover:bg-white sm:right-3 sm:top-3 sm:h-10 sm:w-10"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function FloorPlansPopup() {
  const editMode = useEditMode();
  const isEditMode = Boolean(editMode?.isEditMode);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditMode) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch {
      // sessionStorage unavailable (privacy mode) — still show the popup.
    }
    const id = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [isEditMode]);

  const close = useCallback(() => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    dialogRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Close"]')?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // The editor can't reach a dismissible overlay, so edit mode shows the card inline.
  if (isEditMode) {
    return (
      <section className="bg-muted px-4 py-12 sm:px-6">
        <p className="mx-auto mb-4 max-w-[720px] font-body text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Floor Plans popup — shown as a modal on page load
        </p>
        <div className="mx-auto max-w-[720px]">
          <PopupCard />
        </div>
      </section>
    );
  }

  if (!open) return null;

  return (
    <div
      className="fpp-overlay fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/60 p-4"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="floorplanspopup-heading"
        className="fpp-dialog my-auto w-full max-w-[720px]"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCard onClose={close} />
      </div>

      <style>{`
        .fpp-overlay { animation: fpp-fade 0.25s ease both; }
        .fpp-dialog { animation: fpp-rise 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes fpp-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fpp-rise { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .fpp-overlay, .fpp-dialog { animation: none; }
        }
      `}</style>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansPopup',
  displayName: 'Floor Plans Popup',
  tokens: {
    'floorplanspopup.image': {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    'floorplanspopup.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Introducing Total Monthly Leasing Price *',
    },
    'floorplanspopup.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>We know how important it is to plan your monthly budget with confidence. That’s why our <strong>Total Monthly Leasing Price</strong> shows you the full picture—your base rent along with all required fees. For a personalized estimate, try the <a href="/interactive-map">Calculate My Costs</a> tool in the Interactive Map. You’ll also find a detailed list of possible resident fees at the bottom of the page.</p><p><strong>Because peace of mind starts with transparency.</strong></p>',
    },
  },
};

(FloorPlansPopup as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansPopup;
